/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import style from "../../../styles/common/formModal.module.css";
import EditDialog from "../../../Components/Common/EditDialog";
import FileField from "../../../Components/Masters/FileField";
import formatTitleWithOptional from "../../Scope1/OptionalFeild";
import { isScope12RequiredForInvestmentType, isProjectFinanceInvestmentType, getInvestmentFieldLabel } from "../../../utils/investmentTypeGroups";
import SelectField from "../../../Components/Masters/SelectField";
import { useUser } from "../../../context/AuthContext";
import countries from "../../../Pages/Masters/Roles and Permissions/Country.json";

const EMPTY_PROJECT_FINANCE_FIELDS = {
  isInitialSponsorLender: "",
  expectedAnnualScope1Emission: "",
  expectedAnnualScope2Emission: "",
  projectLifespan: "",
};

const SPONSOR_LENDER_DETAIL_FIELD_NAMES = new Set([
  "expectedAnnualScope1Emission",
  "expectedAnnualScope2Emission",
  "projectLifespan",
]);

const toFormNumberValue = (value) =>
  value === undefined || value === null || value === ""
    ? ""
    : String(value);

const toPayloadNumber = (value) => {
  const num = Number(value);
  return Number.isNaN(num) ? 0 : num;
};

const resolveInitialSponsorLenderValue = (initialData) => {
  if (initialData?.isInitialSponsorLender === true) return "yes";
  if (initialData?.isInitialSponsorLender === false) return "no";

  const investmentType =
    initialData?.investmentType ||
    initialData?.investmentMaster?.investmentType ||
    initialData?.investmentMasterData?.investmentType ||
    "";

  if (isProjectFinanceInvestmentType(investmentType)) return "yes";
  return "";
};

const buildReportingStartYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const lastYear = currentYear - 1;
  const startYear = 2020;
  const totalYears = Math.max(0, lastYear - startYear + 1);
  return Array.from({ length: totalYears }, (_, i) => startYear + i);
};

const buildReportingEndYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const startYear = 2021;
  const totalYears = Math.max(0, currentYear - startYear + 1);
  return Array.from({ length: totalYears }, (_, i) => startYear + i);
};

const getAllowedReportingEndYears = (startYear) => {
  if (Number.isNaN(startYear)) return [];
  return [startYear + 1];
};

const inferPortfolioStartYear = (iso) => {
  const full = toDateInputValue(iso);
  if (!full) return "";
  const [ys, ms, ds] = full.split("-");
  const y = parseInt(ys, 10);
  const m = parseInt(ms, 10);
  const d = parseInt(ds, 10);
  if (m === 4 && d === 1) return ys;
  if (m > 4 || (m === 4 && d > 1)) return String(y);
  if (m < 4) return String(y - 1);
  return String(y);
};

const inferPortfolioEndYear = (iso) => {
  const full = toDateInputValue(iso);
  if (!full) return "";
  const [ys, ms, ds] = full.split("-");
  const y = parseInt(ys, 10);
  const m = parseInt(ms, 10);
  const d = parseInt(ds, 10);
  if (m === 3 && d === 31) return ys;
  if (m < 3 || (m === 3 && d < 31)) return String(y);
  return String(y + 1);
};

const parseYearValue = (value) => {
  const parsed = parseInt(String(value ?? ""), 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const hasPortfolioBasisEnd = (master) => {
  if (!master) return false;
  if (master.hasPortfolioBasisEnd === false) return false;
  if (master.hasPortfolioBasisEnd === true) return true;

  const dateEnd = master.portfolioBasisDateEnd;
  if (dateEnd === null || dateEnd === undefined || dateEnd === "") return false;

  const parsed = new Date(dateEnd);
  return !Number.isNaN(parsed.getTime());
};

const getPortfolioBasisYears = (master) => {
  const start = parseYearValue(
    master?.portfolioBasisStartYear ||
      inferPortfolioStartYear(master?.portfolioBasisDateStart)
  );

  const end = hasPortfolioBasisEnd(master)
    ? parseYearValue(
        master?.portfolioBasisEndYear ||
          inferPortfolioEndYear(master?.portfolioBasisDateEnd)
      )
    : null;

  return { start, end };
};

const filterReportingStartYearsByPortfolio = (years, portfolioStart, portfolioEnd) => {
  if (portfolioStart == null && portfolioEnd == null) return years;
  return years.filter((yr) => {
    if (portfolioStart != null && yr < portfolioStart) return false;
    if (portfolioEnd != null && yr >= portfolioEnd) return false;
    return true;
  });
};

const filterReportingEndYearsByPortfolio = (years, portfolioStart, portfolioEnd) => {
  if (portfolioStart == null && portfolioEnd == null) return years;
  return years.filter((yr) => {
    if (portfolioStart != null && yr <= portfolioStart) return false;
    if (portfolioEnd != null && yr > portfolioEnd) return false;
    return true;
  });
};

const buildReportingStartYearOptionsForMaster = (master) => {
  const { start, end } = getPortfolioBasisYears(master);
  return filterReportingStartYearsByPortfolio(
    buildReportingStartYearOptions(),
    start,
    end
  );
};

const buildReportingEndYearOptionsForMaster = (master, reportingStartYear) => {
  const { start, end } = getPortfolioBasisYears(master);
  let years = filterReportingEndYearsByPortfolio(
    buildReportingEndYearOptions(),
    start,
    end
  );
  const selectedStartYear = parseYearValue(reportingStartYear);
  if (selectedStartYear != null) {
    years = years.filter((yr) =>
      getAllowedReportingEndYears(selectedStartYear).includes(yr)
    );
  }
  return years;
};

const isReportingYearAllowedForMaster = (
  fieldName,
  yearValue,
  master,
  reportingStartYear
) => {
  const year = parseYearValue(yearValue);
  if (year == null) return true;

  const { start, end } = getPortfolioBasisYears(master);
  if (start == null && end == null) {
    if (fieldName === "reportingEndYear") {
      const startYear = parseYearValue(reportingStartYear);
      if (startYear == null) return true;
      return getAllowedReportingEndYears(startYear).includes(year);
    }
    return true;
  }

  if (fieldName === "reportingStartYear") {
    if (start != null && year < start) return false;
    if (end != null && year >= end) return false;
    return true;
  }

  const startYear = parseYearValue(reportingStartYear);
  if (start != null && year <= start) return false;
  if (end != null && year > end) return false;
  if (startYear == null) return true;
  return getAllowedReportingEndYears(startYear).includes(year);
};

const toDateInputValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().split("T")[0];
};

const inferReportingStartYear = (iso) => {
  const full = toDateInputValue(iso);
  if (!full) return "";
  const [ys, ms, ds] = full.split("-");
  const y = parseInt(ys, 10);
  const m = parseInt(ms, 10);
  const d = parseInt(ds, 10);
  if (m === 4 && d === 1) return ys;
  if (m > 4 || (m === 4 && d > 1)) return String(y);
  if (m < 4) return String(y - 1);
  return String(y);
};

const inferReportingEndYear = (iso) => {
  const full = toDateInputValue(iso);
  if (!full) return "";
  const [ys, ms, ds] = full.split("-");
  const y = parseInt(ys, 10);
  const m = parseInt(ms, 10);
  const d = parseInt(ds, 10);
  if (m === 3 && d === 31) return ys;
  if (m < 3 || (m === 3 && d < 31)) return String(y);
  return String(y + 1);
};

const resolveIncludeInScope3FromMaster = (masterRecord) =>
  masterRecord?.includeInScope3 !== undefined
    ? Boolean(masterRecord.includeInScope3)
    : true;

const Form = ({
  isOpen,
  onClose,
  onSubmit,
  header,
  initialData,
  isEditing,
  investmentTypeOptions = [],
  investmentTypeDetailsMap = {},
}) => {
  const { user } = useUser();

  const initialMasterRecord =
    initialData?.investmentMasterData ||
    (typeof initialData?.investmentMaster === "object"
      ? initialData.investmentMaster
      : null);
  const initialIncludeInScope3 =
    resolveIncludeInScope3FromMaster(initialMasterRecord);

  const initialFormData = {
    investmentMaster:
      typeof initialData?.investmentMaster === "object"
        ? initialData?.investmentMaster?._id || ""
        : initialData?.investmentMaster || "",
    investmentType: initialData?.investmentType || "",
    investmentName:
      initialData?.investmentMasterData?.investmentName ||
      initialData?.investmentMaster?.investmentName ||
      initialData?.investmentName ||
      "",
    sector: initialData?.sector || "",
    countryOfOperation: initialData?.countryOfOperation || "",
    equityHoldingPercent: initialData?.equityHoldingPercent ?? "",
    reportingStartYear: inferReportingStartYear(initialData?.reportingStart),
    reportingEndYear: inferReportingEndYear(initialData?.reportingEnd),
    scope1EmissionsMetricTon: initialData?.scope1EmissionsMetricTon ?? "",
    scope2EmissionsMetricTon: initialData?.scope2EmissionsMetricTon ?? "",
    scope3EmissionsMetricTon: initialIncludeInScope3
      ? initialData?.scope3EmissionsMetricTon ?? ""
      : "",
    includeInScope3FromMaster: initialIncludeInScope3,
    isInitialSponsorLender: resolveInitialSponsorLenderValue(initialData),
    expectedAnnualScope1Emission: toFormNumberValue(
      initialData?.expectedAnnualScope1Emission
    ),
    expectedAnnualScope2Emission: toFormNumberValue(
      initialData?.expectedAnnualScope2Emission
    ),
    projectLifespan: toFormNumberValue(initialData?.projectLifespan),
    remark: initialData?.remark || "",
    documents: initialData?.documents || [],
  };

  const [editDialog, setEditDialog] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [formDataSnapshot, setFormDataSnapshot] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const MASTER_LOCKED_FIELDS = [
    "investmentType",
    "sector",
    "countryOfOperation",
  ];

  useEffect(() => {
    setFormData(initialFormData);
    setFormDataSnapshot(initialFormData);
    setUploadedFiles(initialData?.documents || []);
  }, [isOpen, initialData]);

  useEffect(() => {
    if (!isOpen || !formData.investmentName) return;
    const master = investmentTypeDetailsMap?.[formData.investmentName];
    if (!master) return;
    const includeInScope3 = resolveIncludeInScope3FromMaster(master);
    setFormData((prev) => {
      if (prev.includeInScope3FromMaster === includeInScope3) return prev;
      return {
        ...prev,
        includeInScope3FromMaster: includeInScope3,
        scope3EmissionsMetricTon: includeInScope3
          ? prev.scope3EmissionsMetricTon
          : "",
      };
    });
  }, [isOpen, formData.investmentName, investmentTypeDetailsMap]);

  const isScope3EmissionsDisabled =
    Boolean(formData.investmentName) && !formData.includeInScope3FromMaster;

  const selectedMaster = formData.investmentName
    ? investmentTypeDetailsMap?.[formData.investmentName]
    : null;

  const isScope12Required = isScope12RequiredForInvestmentType(
    formData.investmentType
  );

  const isProjectFinance = isProjectFinanceInvestmentType(formData.investmentType);

  const isInitialSponsorLenderYes = formData.isInitialSponsorLender === "yes";

  const isFieldRequired = (field) => {
    if (
      field.name === "scope1EmissionsMetricTon" ||
      field.name === "scope2EmissionsMetricTon"
    ) {
      return isScope12Required;
    }
    if (
      field.sponsorLenderDetailOnly &&
      (!isProjectFinance || !isInitialSponsorLenderYes)
    ) {
      return false;
    }
    return Boolean(field.required);
  };

  const getFieldLabel = (field) => {
    const dynamicLabel = getInvestmentFieldLabel(
      field.name,
      formData.investmentType
    );
    let label = dynamicLabel || field.label;

    if (field.name === "scope3EmissionsMetricTon") {
      label = `${label} (optional)`;
    }

    if (
      (field.name === "scope1EmissionsMetricTon" ||
        field.name === "scope2EmissionsMetricTon") &&
      !isScope12Required
    ) {
      return `${label} (optional)`;
    }
    return label;
  };

  const fieldConfig = [
    {
      label: "Name of investment",
      name: "investmentName",
      type: "select",
      required: true,
      placeholder: "Tap to select",
    },
    {
      label: "Investment Type",
      name: "investmentType",
      type: "text",
      required: true,
      placeholder: "Investment type",
    },
    {
      label: "Sector(s) the investee company operates in",
      name: "sector",
      type: "text",
      required: true,
      placeholder: "Enter sector",
    },
    {
      label: "Country of operation",
      name: "countryOfOperation",
      type: "select",
      required: true,
      placeholder: "Enter country of operation",
    },
    {
      label: "Allocation Share (%)",
      name: "equityHoldingPercent",
      type: "text",
      required: true,
      placeholder: "Enter equity holding",
      numericOnly: true,
    },
    {
      label: "Reporting Period Start Date",
      name: "reportingStartYear",
      type: "reportingYear",
      fixedDateText: "1st April",
      required: true,
    },
    {
      label: "Reporting Period End Date",
      name: "reportingEndYear",
      type: "reportingYear",
      fixedDateText: "31st March",
      required: true,
    },
    {
      label: "Scope 1 Emissions (tCO2e)",
      name: "scope1EmissionsMetricTon",
      type: "text",
      required: true,
      placeholder: "Enter value",
      numericOnly: true,
    },
    {
      label: "Scope 2 Emissions (tCO2e)",
      name: "scope2EmissionsMetricTon",
      type: "text",
      required: true,
      placeholder: "Enter value",
      numericOnly: true,
    },
    {
      label: "Scope 3 Emissions (tCO2e)",
      name: "scope3EmissionsMetricTon",
      type: "text",
      required: false,
      placeholder: "Enter value",
      numericOnly: true,
    },
    {
      label: "Is the Reporting Company the Initial Sponsor/Lender?",
      name: "isInitialSponsorLender",
      type: "boolean",
      required: true,
      projectFinanceOnly: true,
    },
    {
      label: "Expected Annual Scope 1 Emissions",
      name: "expectedAnnualScope1Emission",
      type: "text",
      required: true,
      placeholder: "Enter value",
      numericOnly: true,
      projectFinanceOnly: true,
      sponsorLenderDetailOnly: true,
    },
    {
      label: "Expected Annual Scope 2 Emissions",
      name: "expectedAnnualScope2Emission",
      type: "text",
      required: true,
      placeholder: "Enter value",
      numericOnly: true,
      projectFinanceOnly: true,
      sponsorLenderDetailOnly: true,
    },
    {
      label: "Project Lifetime (Years)",
      name: "projectLifespan",
      type: "text",
      required: true,
      placeholder: "Enter value",
      numericOnly: true,
      integerOnly: true,
      projectFinanceOnly: true,
      sponsorLenderDetailOnly: true,
    },
    {
      label: "Remark (optional)",
      name: "remark",
      type: "text",
      required: false,
      placeholder: "Enter remark",
    },
    {
      name: "document",
      type: "docs",
      placeholder: "Enter Document",
    },
  ];

  const handleExitAgree = () => {
    setEditDialog(false);
    setErrors({});
    setFormData(initialFormData);
    setFormDataSnapshot(initialFormData);
    setUploadedFiles([]);
    onClose();
  };

  const isFormChanged = () =>
    JSON.stringify(formData) !== JSON.stringify(formDataSnapshot);

  const handleClose = () => {
    if (!isFormChanged()) {
      handleExitAgree();
    } else {
      setEditDialog(true);
    }
  };

  const validateFormData = () => {
    const newErrors = {};

    fieldConfig.forEach((field) => {
      const value = formData[field.name];

      if (
        field?.name === "scope3EmissionsMetricTon" &&
        isScope3EmissionsDisabled
      ) {
        return;
      }

      if (field.projectFinanceOnly && !isProjectFinance) {
        return;
      }

      if (field.sponsorLenderDetailOnly && !isInitialSponsorLenderYes) {
        return;
      }

      if (isFieldRequired(field)) {
        if (
          value === "" ||
          value === null ||
          value === undefined ||
          value === "Tap to select"
        ) {
          newErrors[field.name] = `${getFieldLabel(field)} is required.`;
          return;
        }
      }

      if (
        (field.type === "number" || field.numericOnly) &&
        value !== "" &&
        isNaN(Number(value))
      ) {
        newErrors[field.name] = `${getFieldLabel(field)} must be a number.`;
      }

      if (
        field.integerOnly &&
        value !== "" &&
        !/^\d+$/.test(String(value))
      ) {
        newErrors[field.name] = `${getFieldLabel(field)} must be a whole number.`;
      }
    });

    if (isScope3EmissionsDisabled) {
      delete newErrors.scope3EmissionsMetricTon;
    }

    if (!isInitialSponsorLenderYes) {
      SPONSOR_LENDER_DETAIL_FIELD_NAMES.forEach((fieldName) => {
        delete newErrors[fieldName];
      });
    }

    if (formData.reportingStartYear && formData.reportingEndYear) {
      const startYear = parseInt(formData.reportingStartYear, 10);
      const endYear = parseInt(formData.reportingEndYear, 10);
      const allowedEndYears = getAllowedReportingEndYears(startYear);
      if (!Number.isNaN(endYear) && !allowedEndYears.includes(endYear)) {
        newErrors.reportingEndYear =
          "Reporting period end year must be the immediate next year of the start year.";
      }
    }

    if (
      formData.reportingStartYear &&
      !isReportingYearAllowedForMaster(
        "reportingStartYear",
        formData.reportingStartYear,
        selectedMaster,
        formData.reportingStartYear
      )
    ) {
      const { start } = getPortfolioBasisYears(selectedMaster);
      newErrors.reportingStartYear = hasPortfolioBasisEnd(selectedMaster)
        ? "Reporting period start year must be on or after the portfolio basis start year and before the portfolio basis end year."
        : start != null
        ? "Reporting period start year must be on or after the portfolio basis start year."
        : "Reporting period start year is invalid.";
    }

    if (
      formData.reportingEndYear &&
      !isReportingYearAllowedForMaster(
        "reportingEndYear",
        formData.reportingEndYear,
        selectedMaster,
        formData.reportingStartYear
      )
    ) {
      newErrors.reportingEndYear = hasPortfolioBasisEnd(selectedMaster)
        ? "Reporting period end year must be after the portfolio basis start year and on or before the portfolio basis end year."
        : "Reporting period end year must be after the portfolio basis start year.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (name, type) => (event) => {
    const { value } = event.target;

    // Check if field should only accept numeric values
    const field = fieldConfig.find((f) => f.name === name);
    const isNumericField = type === "number" || field?.numericOnly;

    if (isNumericField && value !== "" && !/^\d*\.?\d*$/.test(value)) return;
    if (field?.integerOnly && value !== "" && !/^\d*$/.test(value)) return;

    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      };

      if (name === "reportingStartYear") {
        const startYear = parseInt(value, 10);
        const endYear = parseInt(prev.reportingEndYear, 10);
        const allowedEndYears = getAllowedReportingEndYears(startYear);
        if (!Number.isNaN(endYear) && !allowedEndYears.includes(endYear)) {
          updated.reportingEndYear = "";
        }
      }

      if (name === "isInitialSponsorLender" && value !== "yes") {
        updated.expectedAnnualScope1Emission = "";
        updated.expectedAnnualScope2Emission = "";
        updated.projectLifespan = "";
      }

      return updated;
    });

    setErrors((prev) => {
      const next = { ...prev, [name]: "" };
      if (name === "isInitialSponsorLender" && value !== "yes") {
        SPONSOR_LENDER_DETAIL_FIELD_NAMES.forEach((fieldName) => {
          next[fieldName] = "";
        });
      }
      return next;
    });
  };

  const handleSubmit = () => {
    if (!validateFormData()) return;

    let formDataToSubmit = {
      ...formData,
      reportingStart: formData.reportingStartYear
        ? `${formData.reportingStartYear}-04-01`
        : "",
      reportingEnd: formData.reportingEndYear
        ? `${formData.reportingEndYear}-03-31`
        : "",
      documents: uploadedFiles,
    };
    delete formDataToSubmit.reportingStartYear;
    delete formDataToSubmit.reportingEndYear;
    delete formDataToSubmit.investmentName;
    delete formDataToSubmit.includeInScope3FromMaster;
    if (isScope3EmissionsDisabled) {
      formDataToSubmit.scope3EmissionsMetricTon = "";
    }

    if (isProjectFinance) {
      formDataToSubmit.isInitialSponsorLender =
        formData.isInitialSponsorLender === "yes";
      if (formData.isInitialSponsorLender === "yes") {
        formDataToSubmit.expectedAnnualScope1Emission = toPayloadNumber(
          formData.expectedAnnualScope1Emission
        );
        formDataToSubmit.expectedAnnualScope2Emission = toPayloadNumber(
          formData.expectedAnnualScope2Emission
        );
        formDataToSubmit.projectLifespan = toPayloadNumber(formData.projectLifespan);
      } else {
        formDataToSubmit.expectedAnnualScope1Emission = null;
        formDataToSubmit.expectedAnnualScope2Emission = null;
        formDataToSubmit.projectLifespan = null;
      }
    } else {
      delete formDataToSubmit.isInitialSponsorLender;
      delete formDataToSubmit.expectedAnnualScope1Emission;
      delete formDataToSubmit.expectedAnnualScope2Emission;
      delete formDataToSubmit.projectLifespan;
    }

    if (user?.endUser && isEditing) {
      formDataToSubmit.requestStatus = "edit";
      formDataToSubmit.requestMode = true;
      formDataToSubmit.approvalStatus = "Pending";
    }

    onSubmit(formDataToSubmit, isEditing);
    setUploadedFiles([]);
  };

  return (
    <Drawer
      sx={{
        backgroundColor: "transparent",
        "& .MuiDrawer-paper": {
          backgroundColor: "transparent",
          boxShadow: "none",
          height: "100vh",
          minHeight: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
      anchor="right"
      open={isOpen}
      onClose={handleClose}
    >
      <Box
        sx={{
          width: "26rem",
          display: "flex",
          justifyContent: "space-between",
          gap: "1rem",
        }}
      >
        {editDialog && (
          <EditDialog {...{ editDialog, setEditDialog, handleExitAgree }} />
        )}
        <Box
          sx={{
            backgroundColor: "#fff",
            height: "20px",
            padding: "13px",
            margin: "30px 0 0 0",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "80%",
            width: "5%",
            zIndex: "1",
          }}
        >
          <IconButton onClick={handleClose} sx={{ color: "black" }}>
            <CloseIcon sx={{ fontSize: "1.2rem" }} />
          </IconButton>
        </Box>
        <Box className={style.main_div}>
          <Typography
            id="modal-title"
            variant="h6"
            align="left"
            sx={{ textDecoration: "underline", fontWeight: "bold", mb: 2 }}
          >
            {header}
          </Typography>

          <form>
            {fieldConfig.map((field) => {
              if (
                field?.name === "scope3EmissionsMetricTon" &&
                isScope3EmissionsDisabled
              ) {
                return null;
              }

              if (field.projectFinanceOnly && !isProjectFinance) {
                return null;
              }

              if (field.sponsorLenderDetailOnly && !isInitialSponsorLenderYes) {
                return null;
              }

              return (
              <div key={field.name} style={{ margin: "1rem 0" }}>
                {field.type === "docs" ? (
                  <FileField
                    title={formatTitleWithOptional("Upload Documents (optional)")}
                    name={field.name}
                    errors={errors[field.name]}
                    fileUrls={uploadedFiles}
                    progress={uploadProgress[field.name] || []}
                    {...{
                      setUploadedFiles,
                      setUploadProgress,
                      setErrors,
                    }}
                  />
                ) : field.type === "select" && field.name === "investmentName" ? (
                  <SelectField
                    title={field.label}
                    name={field.name}
                    value={formData[field.name]}
                    handleChange={(e) => {
                      const value =
                        e.target.value === "Tap to select" ? "" : e.target.value;
                      const selectedMaster = investmentTypeDetailsMap?.[value] || {};
                      const includeInScope3 =
                        resolveIncludeInScope3FromMaster(selectedMaster);
                      const nextInvestmentType =
                        value && selectedMaster.investmentType
                          ? selectedMaster.investmentType
                          : "";
                      setFormData((prev) => ({
                        ...prev,
                        investmentMaster:
                          value && selectedMaster?._id ? selectedMaster._id : "",
                        investmentName: value,
                        investmentType: nextInvestmentType,
                        sector:
                          value && selectedMaster.sector
                            ? selectedMaster.sector
                            : "",
                        countryOfOperation:
                          value && selectedMaster.countryOfOperation
                            ? selectedMaster.countryOfOperation
                            : "",
                        includeInScope3FromMaster: value ? includeInScope3 : true,
                        scope3EmissionsMetricTon:
                          value && !includeInScope3
                            ? ""
                            : prev.scope3EmissionsMetricTon,
                        reportingStartYear: "",
                        reportingEndYear: "",
                        ...(isProjectFinanceInvestmentType(nextInvestmentType)
                          ? { isInitialSponsorLender: "yes" }
                          : EMPTY_PROJECT_FINANCE_FIELDS),
                      }));
                      setErrors((prev) => ({
                        ...prev,
                        [field.name]: "",
                        investmentType: "",
                        sector: "",
                        countryOfOperation: "",
                        scope3EmissionsMetricTon: "",
                      }));
                    }}
                    options={investmentTypeOptions}
                    errors={errors[field.name]}
                  />
                ) : field.type === "select" && field.name === "countryOfOperation" ? (
                  <SelectField
                    title={field.label}
                    name={field.name}
                    value={formData[field.name]}
                    disable={MASTER_LOCKED_FIELDS.includes(field.name)}
                    disabledGrey={MASTER_LOCKED_FIELDS.includes(field.name)}
                    handleChange={(e) => {
                      const value =
                        e.target.value === "Tap to select" ? "" : e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        [field.name]: value,
                      }));
                      setErrors((prev) => ({
                        ...prev,
                        [field.name]: "",
                      }));
                    }}
                    options={countries.countries.map((country) => country.name)}
                    errors={errors[field.name]}
                  />
                ) : field.type === "reportingYear" ? (
                  <>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mt: 1 }}
                    >
                      {field.label}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        flexWrap: "nowrap",
                      }}
                    >
                      <TextField
                        size="small"
                        value={field.fixedDateText}
                        disabled
                        sx={{
                          width: 130,
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: "#6b7280",
                            color: "#6b7280",
                            cursor: "not-allowed",
                          },
                          "& .MuiInputBase-root.Mui-disabled": {
                            backgroundColor: "#f3f4f6",
                            cursor: "not-allowed",
                          },
                          "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: "rgba(0, 0, 0, 0.23)",
                            },
                        }}
                      />
                      <TextField
                        select
                        size="small"
                        required={field.required}
                        sx={{
                          width: 130,
                          "& .MuiSelect-select": { fontStyle: "normal" },
                        }}
                        value={formData[field.name] || ""}
                        onChange={handleChange(field.name, field.type)}
                        error={Boolean(errors[field.name])}
                        FormHelperTextProps={{
                          sx: { ml: 0, mr: 0, fontSize: "0.875rem" },
                        }}
                        SelectProps={{ displayEmpty: true }}
                      >
                        <MenuItem value="" sx={{ fontStyle: "normal" }}>
                          Select year
                        </MenuItem>
                        {(field.name === "reportingStartYear"
                          ? buildReportingStartYearOptionsForMaster(selectedMaster)
                          : buildReportingEndYearOptionsForMaster(
                              selectedMaster,
                              formData.reportingStartYear
                            )
                        ).map((yr) => (
                            <MenuItem key={yr} value={String(yr)}>
                              {yr}
                            </MenuItem>
                          ))}
                      </TextField>
                    </Box>
                    {errors[field.name] ? (
                      <Typography
                        variant="body2"
                        color="#D34040"
                        sx={{ mt: 0.5, ml: 0 }}
                      >
                        {errors[field.name]}
                      </Typography>
                    ) : null}
                  </>
                ) : field.type === "boolean" ? (
                  <>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mt: 1 }}
                    >
                      {field.label}
                    </Typography>
                    <RadioGroup
                      row
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange(field.name, field.type)}
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio />}
                        label="Yes"
                      />
                      <FormControlLabel value="no" control={<Radio />} label="No" />
                    </RadioGroup>
                    {errors[field.name] ? (
                      <Typography
                        variant="body2"
                        color="#D34040"
                        sx={{ mt: 0.5, ml: 0 }}
                      >
                        {errors[field.name]}
                      </Typography>
                    ) : null}
                  </>
                ) : field.type === "readonly" ? (
                  <>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mt: 1 }}
                    >
                      {field.label}
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      disabled
                      value={formData[field.name] ?? ""}
                      placeholder={field.placeholder || "-"}
                      sx={{
                        "& .MuiInputBase-input.Mui-disabled": {
                          WebkitTextFillColor: "#6b7280",
                          color: "#6b7280",
                          cursor: "not-allowed",
                        },
                        "& .MuiInputBase-root.Mui-disabled": {
                          backgroundColor: "#f3f4f6",
                          cursor: "not-allowed",
                        },
                      }}
                    />
                  </>
                ) : (
                  <>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mt: 1 }}
                    >
                      {formatTitleWithOptional(getFieldLabel(field))}
                    </Typography>

                    <TextField
                      type={field.type === "date" ? "date" : "text"}
                      size="small"
                      fullWidth
                      disabled={MASTER_LOCKED_FIELDS.includes(field.name)}
                      required={isFieldRequired(field)}
                      placeholder={field.placeholder || ""}
                      multiline={field.name === "remark"}
                      minRows={field.name === "remark" ? 2 : 1}
                      value={formData[field.name] ?? ""}
                      onChange={handleChange(field.name, field.type)}
                      error={Boolean(errors[field.name])}
                      helperText={errors[field.name] || ""}
                      FormHelperTextProps={{
                        sx: { ml: 0, mr: 0, fontSize: "0.875rem" },
                      }}
                      inputProps={
                        field.type === "date"
                          ? { max: new Date().toISOString().split("T")[0] }
                          : {}
                      }
                      sx={{
                        ...(MASTER_LOCKED_FIELDS.includes(field.name) && {
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: "#6b7280",
                            color: "#6b7280",
                            cursor: "not-allowed",
                          },
                          "& .MuiInputBase-root.Mui-disabled": {
                            backgroundColor: "#f3f4f6",
                            cursor: "not-allowed",
                          },
                        }),
                      }}
                    />
                  </>
                )}
              </div>
              );
            })}
          </form>

          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ backgroundColor: "#1C486B", textTransform: "none" }}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Form;

